// =====================================================
// Review Queue Service
// Handles transaction review workflow before tax export
// =====================================================

import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/types/database';

type TransactionTableRow = Database['public']['Tables']['transactions']['Row'];
type TransactionUpdate =
  Database['public']['Tables']['transactions']['Update'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const toStringOrEmpty = (value: unknown): string =>
  typeof value === 'string' ? value : '';

const toStringOrNull = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

const toNumberOrZero = (value: unknown): number =>
  typeof value === 'number' ? value : 0;

const toNumberOrNull = (value: unknown): number | null =>
  typeof value === 'number' ? value : null;

const extractClassification = (
  classification: unknown,
): Record<string, unknown> =>
  isRecord(classification) ? classification : {};

const mapTransactionRow = (
  row: TransactionTableRow | Record<string, unknown>,
): ReviewTransaction => {
  const baseRow = row as TransactionTableRow;
  const fallback = isRecord(row) ? row : {};
  const classification = extractClassification(baseRow.ai_classification);

  const debitAccount =
    'debit_account' in fallback
      ? toStringOrNull(fallback.debit_account)
      : null;

  const creditAccount =
    'credit_account' in fallback
      ? toStringOrNull(fallback.credit_account)
      : null;

  const counterparty = toStringOrEmpty(baseRow.counterparty);

  // Map booking_status to classification_status since classification_status doesn't exist in schema
  const classificationStatus = baseRow.booking_status === 'booked' 
    ? 'classified' 
    : baseRow.booking_status === 'draft'
    ? 'pending'
    : toStringOrNull(baseRow.booking_status);

  return {
    id: toStringOrEmpty(baseRow.id),
    date: toStringOrEmpty(baseRow.transaction_date),
    counterparty,
    description: toStringOrEmpty(baseRow.description),
    amount: toNumberOrZero(baseRow.amount),
    debit_account: debitAccount,
    credit_account: creditAccount,
    vat_rate: toNumberOrNull(classification.vatRate),
    vat_code: toStringOrNull(classification.vatCode),
    classification_confidence: toNumberOrNull(
      baseRow.ai_classification_confidence,
    ),
    classification_source: toStringOrNull(classification.source),
    classification_status: classificationStatus,
    mandate_id: toStringOrEmpty(baseRow.mandate_id),
    journal_entry_id: toStringOrNull(baseRow.journal_entry_id),
    booking_status: toStringOrNull(baseRow.booking_status),
  };
};

export interface ReviewTransaction {
  id: string;
  date: string;
  counterparty: string;
  description: string;
  amount: number;
  debit_account: string | null;
  credit_account: string | null;
  vat_rate: number | null;
  vat_code: string | null;
  classification_confidence: number | null;
  classification_source: string | null;
  classification_status: string | null;
  mandate_id: string;
  journal_entry_id: string | null;
  booking_status: string | null;
}

export interface ReviewQueueFilters {
  mandateId?: string;
  status?: 'all' | 'uncertain' | 'missing_vat' | 'has_receipt';
  sortBy?: 'date' | 'confidence' | 'amount';
  sortOrder?: 'asc' | 'desc';
}

export interface VATWarning {
  transactionId: string;
  counterparty: string;
  currentVAT: number;
  expectedVAT: number;
  reason: string;
  category: string;
}

/**
 * Fetch transactions for review queue
 */
export async function getTransactionsForReview(
  mandateId: string,
  filters: ReviewQueueFilters = {}
): Promise<ReviewTransaction[]> {
  try {
    // Map interface field names to database column names
    const sortByMapping: Record<string, string> = {
      'date': 'transaction_date',
      'confidence': 'ai_classification_confidence',
      'amount': 'amount'
    };
    
    const dbSortBy = sortByMapping[filters.sortBy || 'date'] || 'transaction_date';
    
    let query = supabase
      .from('transactions')
      .select('*')
      .eq('mandate_id', mandateId)
      .order(dbSortBy, { ascending: filters.sortOrder === 'asc' });

    // Filter by classification status
    if (filters.status === 'uncertain') {
      query = query.lt('ai_classification_confidence', 80);
    }
    // Note: missing_vat filter removed since VAT is in ai_classification JSONB

    const { data, error } = await query;

    if (error) throw error;

    const rows = Array.isArray(data) ? data : [];

    return rows.map(mapTransactionRow);
  } catch (error) {
    console.error('[Review Queue] Error fetching transactions:', error);
    throw error;
  }
}

/**
 * Approve a transaction (marks it as reviewed)
 * Once Agent 2 adds review_status, this will update that field
 */
export async function approveTransaction(transactionId: string): Promise<void> {
  try {
    // For now, just log approval
    // Once review_status field exists, we'll update that instead
    const payload: Partial<TransactionUpdate> = {
      updated_at: new Date().toISOString(),
    };

    const { error } = await supabase
      .from('transactions')
      .update(payload)
      .eq('id', transactionId);

    if (error) throw error;

    console.log(`[Review Queue] Approved transaction ${transactionId}`);
  } catch (error) {
    console.error('[Review Queue] Error approving transaction:', error);
    throw error;
  }
}

/**
 * Update transaction classification
 */
export async function updateTransaction(
  transactionId: string,
  updates: {
    debit_account?: string;
    credit_account?: string;
    vat_rate?: number;
    vat_code?: string;
  }
): Promise<void> {
  try {
    // Get current transaction to update ai_classification
    const { data: transaction, error: fetchError } = await supabase
      .from('transactions')
      .select('ai_classification')
      .eq('id', transactionId)
      .single();
    
    if (fetchError) throw fetchError;
    
    const payload: Partial<TransactionUpdate> = {
      updated_at: new Date().toISOString(),
    };

    if (typeof updates.debit_account === 'string') {
      payload.account_number = updates.debit_account;
    }

    if (updates.vat_rate !== undefined || updates.vat_code !== undefined) {
      const aiClassification = extractClassification(
        transaction?.ai_classification,
      );

      if (updates.vat_rate !== undefined) {
        aiClassification.vatRate = updates.vat_rate;
      }

      if (updates.vat_code !== undefined) {
        aiClassification.vatCode = updates.vat_code;
      }

      payload.ai_classification = aiClassification as any;
    }

    const { error } = await supabase
      .from('transactions')
      .update(payload)
      .eq('id', transactionId);

    if (error) throw error;

    console.log(`[Review Queue] Updated transaction ${transactionId}`);
  } catch (error) {
    console.error('[Review Queue] Error updating transaction:', error);
    throw error;
  }
}

/**
 * Reject/delete a transaction
 */
export async function rejectTransaction(transactionId: string): Promise<void> {
  try {
    // Mark as rejected by deleting
    const { error } = await supabase
      .from('transactions')
      .delete()
      .eq('id', transactionId);

    if (error) throw error;

    console.log(`[Review Queue] Rejected transaction ${transactionId}`);
  } catch (error) {
    console.error('[Review Queue] Error rejecting transaction:', error);
    throw error;
  }
}

/**
 * Bulk approve transactions
 */
export async function bulkApproveTransactions(transactionIds: string[]): Promise<void> {
  try {
    const payload: Partial<TransactionUpdate> = {
      updated_at: new Date().toISOString(),
    };

    const { error } = await supabase
      .from('transactions')
      .update(payload)
      .in('id', transactionIds);

    if (error) throw error;

    console.log(`[Review Queue] Bulk approved ${transactionIds.length} transactions`);
  } catch (error) {
    console.error('[Review Queue] Error bulk approving:', error);
    throw error;
  }
}

/**
 * Validate VAT for Swiss transactions
 * Returns warnings for suspicious VAT assignments
 */
export function validateTransactionVAT(transaction: ReviewTransaction): VATWarning | null {
  const counterparty = (transaction.counterparty || '').toLowerCase();
  const description = (transaction.description || '').toLowerCase();
  const currentVAT = transaction.vat_rate || 0;

  // Swiss VAT rules
  const vatRules = [
    {
      category: 'meals',
      expectedVAT: 8.1,
      keywords: ['restaurant', 'café', 'imbiss', 'mensa', 'kantine', 'pizzeria', 'kebab', 'burger'],
    },
    {
      category: 'services',
      expectedVAT: 8.1,
      keywords: ['consulting', 'beratung', 'software', 'marketing', 'design', 'development'],
    },
    {
      category: 'accommodation',
      expectedVAT: 3.8,
      keywords: ['hotel', 'airbnb', 'übernachtung', 'unterkunft'],
    },
    {
      category: 'exports',
      expectedVAT: 0,
      keywords: ['google', 'amazon', 'microsoft', 'aws', 'stripe', 'paypal'],
    },
    {
      category: 'exempt',
      expectedVAT: 0,
      keywords: ['versicherung', 'insurance', 'bank', 'krankenkasse', 'post', 'sbb', 'gebühr'],
    },
  ];

  // Check each rule
  for (const rule of vatRules) {
    const matches = rule.keywords.some(
      keyword => counterparty.includes(keyword) || description.includes(keyword)
    );

    if (matches && Math.abs(currentVAT - rule.expectedVAT) > 0.1) {
      return {
        transactionId: transaction.id,
        counterparty: transaction.counterparty,
        currentVAT,
        expectedVAT: rule.expectedVAT,
        reason: `Looks like ${rule.category}`,
        category: rule.category,
      };
    }
  }

  return null;
}

/**
 * Get VAT warnings for a specific transaction
 */
export async function getVATWarnings(transactionId: string): Promise<VATWarning | null> {
  try {
    const { data, error } = await supabase
      .from('transactions')
      .select('*')
      .eq('id', transactionId)
      .single();

    if (error) throw error;
    if (!data) return null;

    const reviewTransaction = mapTransactionRow(data);
    return validateTransactionVAT(reviewTransaction);
  } catch (error) {
    console.error('[Review Queue] Error getting VAT warnings:', error);
    return null;
  }
}

/**
 * Bulk set VAT rate for multiple transactions
 * NOTE: VAT rate is stored in ai_classification JSON, not as direct field
 */
export async function bulkSetVATRate(transactionIds: string[], vatRate: number): Promise<void> {
  try {
    // TODO: Properly update ai_classification JSON with vatRate
    // For now, we'll update the transactions to trigger a re-classification
    const payload: Partial<TransactionUpdate> = {
      // vat_rate field doesn't exist in schema - stored in ai_classification JSON
      updated_at: new Date().toISOString(),
    };

    const { error } = await supabase
      .from('transactions')
      .update(payload)
      .in('id', transactionIds);

    if (error) throw error;

    console.log(`[Review Queue] Bulk set VAT rate to ${vatRate}% for ${transactionIds.length} transactions (requires ai_classification update)`);
  } catch (error) {
    console.error('[Review Queue] Error bulk setting VAT rate:', error);
    throw error;
  }
}

/**
 * Bulk assign debit account for multiple transactions
 */
export async function bulkAssignAccount(transactionIds: string[], debitAccount: string): Promise<void> {
  try {
    const payload: Partial<TransactionUpdate> = {
      account_number: debitAccount,
      updated_at: new Date().toISOString(),
    };

    const { error } = await supabase
      .from('transactions')
      .update(payload)
      .in('id', transactionIds);

    if (error) throw error;

    console.log(`[Review Queue] Bulk assigned account ${debitAccount} to ${transactionIds.length} transactions`);
  } catch (error) {
    console.error('[Review Queue] Error bulk assigning account:', error);
    throw error;
  }
}

/**
 * Get review progress for a mandate
 */
export async function getReviewProgress(mandateId: string): Promise<{
  total: number;
  pending: number;
  approved: number;
  edited: number;
  rejected: number;
  percentComplete: number;
}> {
  try {
    const { data, error } = await supabase
      .from('transactions')
      .select('id')
      .eq('mandate_id', mandateId);

    if (error) throw error;

    const total = data?.length || 0;
    // Since classification_status doesn't exist in types, we'll just return basic stats
    const approved = 0;
    const edited = 0;
    const rejected = 0;
    const pending = total;
    const percentComplete = 0;

    return {
      total,
      pending,
      approved,
      edited,
      rejected,
      percentComplete,
    };
  } catch (error) {
    console.error('[Review Queue] Error getting progress:', error);
    throw error;
  }
}
